import React, { useState, useEffect, Fragment } from 'react';
import { Breadcrumbs, H5 } from '../../../AbstractElements';
import { Card, CardBody, CardHeader, Col, Container, Row, Input, FormGroup, Label } from 'reactstrap';
import DataTable from '../../Common/DataTable';
import { Edit } from 'react-feather';
import { fetchTableData, fetchFormData } from '../../../Api/ApiMethods';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { AccidentDateFilter, CustomerNameFilter, CustomerNumberFilter, PlateFilter } from '../../Common/Filters/CommonFilters';
import { actionButtonColumn, customerDetailButtonColumn } from '../../Common/TableColumns';
import { formatDateTR } from '../../../_helper/formatters';
import useDebouncedValue from '../../../_helper/useDebouncedValue';
import { appendQueryParamIfMinLength, normalizeSearchFields, normalizeSelectOptions } from '../../../_helper/regexHandler';

const AccidentFiles = () => {
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedFileTypes, setSelectedFileTypes] = useState([]);
    const [selectedOpenClosed, setSelectedOpenClosed] = useState('');
    const [selectedFileStatus, setSelectedFileStatus] = useState('');
    const [selectedFileStatusGroup, setSelectedFileStatusGroup] = useState(null);
    const [customerNumberSearch, setCustomerNumberSearch] = useState('');
    const [customerNameSearch, setCustomerNameSearch] = useState('');
    const [accidentDateSearch, setAccidentDateSearch] = useState('');
    const [plateSearch, setPlateSearch] = useState('');
    const [caseNumberSearch, setCaseNumberSearch] = useState('');
    const [fileTypeOptions, setFileTypeOptions] = useState([]);
    const [fileCaseStatusOptions, setFileCaseStatusOptions] = useState({ nonEnforcement: [], enforcement: [], callForm: [] });

    const debouncedCustomerNumberSearch = useDebouncedValue(customerNumberSearch, 500);
    const debouncedCustomerNameSearch = useDebouncedValue(customerNameSearch, 500);
    const debouncedAccidentDateSearch = useDebouncedValue(accidentDateSearch, 500);
    const debouncedPlateSearch = useDebouncedValue(plateSearch, 500);
    const debouncedCaseNumberSearch = useDebouncedValue(caseNumberSearch, 500);

    const accidentFileApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/accidentfile';
    const workFileTypeApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/workfiletype';
    const arbitrationStateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/ArbitrationState';
    const callFormStateApiUrl = process.env.REACT_APP_LACIVERT_API_ENDPOINT + '/callformstate';
    const navigate = useNavigate();

    useEffect(() => {
        fetchTableData({
            apiEndpoint: workFileTypeApiUrl,
            page: 1,
            pageSize: 1000,
            search: null,
            setDataCallback: (items) => setFileTypeOptions(items || []),
            setTotalRowsCallback: () => {},
        });
    }, [workFileTypeApiUrl]);

    useEffect(() => {
        const fetchStatuses = async () => {
            try {
                const buildUrl = (isEnforcement) => {
                    const params = new URLSearchParams({ isEnforcement: String(isEnforcement), page: '1', pageSize: '200' });
                    return `${arbitrationStateApiUrl}?${params.toString()}`;
                };

                const [nonEnfResp, enfResp, callFormResp] = await Promise.all([
                    fetchFormData({ apiEndpoint: buildUrl(false), page: 1, pageSize: 200 }),
                    fetchFormData({ apiEndpoint: buildUrl(true), page: 1, pageSize: 200 }),
                    fetchFormData({ apiEndpoint: callFormStateApiUrl, page: 1, pageSize: 200 }),
                ]);

                setFileCaseStatusOptions({
                    nonEnforcement: normalizeSelectOptions(nonEnfResp),
                    enforcement: normalizeSelectOptions(enfResp),
                    callForm: normalizeSelectOptions(callFormResp),
                });
            } catch (error) {
                console.error('Error fetching arbitration states for status filter', error);
                setFileCaseStatusOptions({ nonEnforcement: [], enforcement: [], callForm: [] });
            }
        };

        fetchStatuses();
    }, [arbitrationStateApiUrl, callFormStateApiUrl]);

    useEffect(() => {
        let isMounted = true;
        const fetchAccidentFiles = async () => {
            const { normalized, hasInvalid } = normalizeSearchFields([
                { key: 'customerNumber', value: debouncedCustomerNumberSearch, minLength: 3 },
                { key: 'customerName', value: debouncedCustomerNameSearch, minLength: 2 },
                { key: 'plate', value: debouncedPlateSearch, minLength: 2 },
                { key: 'accidentDate', value: debouncedAccidentDateSearch, minLength: 3 },
                { key: 'caseNumber', value: debouncedCaseNumberSearch, minLength: 3 }
            ]);

            if (hasInvalid) {
                if (isMounted) setIsLoading(false);
                return;
            }

            setIsLoading(true);
            const queryParams = new URLSearchParams();

            if (selectedFileTypes.length > 0) {
                queryParams.append('workFileType', selectedFileTypes.join(','));
            }

            // Ensure status param aligns with current group, even after options update
            if (selectedFileStatus && !selectedFileStatusGroup) {
                const findGroup = (value) => {
                    if (!value) return null;
                    if (fileCaseStatusOptions.callForm.some(opt => opt.value === value)) return 'callForm';
                    if (fileCaseStatusOptions.nonEnforcement.some(opt => opt.value === value)) return 'nonEnforcement';
                    if (fileCaseStatusOptions.enforcement.some(opt => opt.value === value)) return 'enforcement';
                    return null;
                };
                setSelectedFileStatusGroup(findGroup(selectedFileStatus));
            }

            if (selectedOpenClosed === 'open') {
                queryParams.append('fileIsClosed', 'false');
            } else if (selectedOpenClosed === 'closed') {
                queryParams.append('fileIsClosed', 'true');
            }

            if (selectedFileStatus) {
                if (selectedFileStatusGroup === 'callForm') {
                    queryParams.append('fileStatus', selectedFileStatus);
                } else if (selectedFileStatusGroup === 'nonEnforcement' || selectedFileStatusGroup === 'enforcement') {
                    queryParams.append('arbitrationFileStatus', selectedFileStatus);
                } else {
                    queryParams.append('fileStatus', selectedFileStatus);
                }
            }

            appendQueryParamIfMinLength(queryParams, 'tcId', normalized.customerNumber, 3);
            appendQueryParamIfMinLength(queryParams, 'customerNameOrCompanyName', normalized.customerName, 2);
            appendQueryParamIfMinLength(queryParams, 'accidentDate', normalized.accidentDate, 3);
            appendQueryParamIfMinLength(queryParams, 'licensePlate', normalized.plate, 2);
            appendQueryParamIfMinLength(queryParams, 'caseNumber', normalized.caseNumber, 3);

            const apiUrl = queryParams.toString()
                ? `${accidentFileApiUrl}?${queryParams.toString()}`
                : accidentFileApiUrl;

            await fetchTableData({
                apiEndpoint: apiUrl,
                page,
                pageSize,
                search: null,
                setDataCallback: setData,
                setTotalRowsCallback: setTotalRows,
            });

            if (isMounted) setIsLoading(false);
        };

        fetchAccidentFiles();

        return () => {
            isMounted = false;
        };
    }, [
        page,
        pageSize,
        selectedFileTypes,
        selectedOpenClosed,
        selectedFileStatus,
        debouncedCustomerNumberSearch,
        debouncedCustomerNameSearch,
        debouncedAccidentDateSearch,
        debouncedPlateSearch,
        debouncedCaseNumberSearch
    ]);

    useEffect(() => {
        // Derive file status options from fetched data
        const statusSet = new Set();
        data.forEach(item => {
            if (item.fileCaseStatus) statusSet.add(item.fileCaseStatus);
        });

        // Keep backend status options, but if API returns none, fall back to derived
        if (
            fileCaseStatusOptions.nonEnforcement.length === 0 &&
            fileCaseStatusOptions.enforcement.length === 0 &&
            fileCaseStatusOptions.callForm.length === 0 &&
            statusSet.size > 0
        ) {
            const fallback = Array.from(statusSet).map(s => ({ value: s, label: s }));
            setFileCaseStatusOptions({ nonEnforcement: fallback, enforcement: [], callForm: [] });
        }
    }, [data, fileCaseStatusOptions]);

    const fileTypeSelectOptions = normalizeSelectOptions(fileTypeOptions, ['title', 'name']);
    const selectedFileTypeSelectValues = fileTypeSelectOptions.filter(opt => selectedFileTypes.includes(opt.value));

    const handleCustomerDetail = (customerId) => {
        navigate(`/customer/${customerId}`);
    };

    const handleCallForm = (row) => {
        const callFormId = row.callForm?.[0];
        
        if (callFormId) {
            navigate(`/callform/${callFormId}`);
        } else {
            toast.error('Bu kaza dosyası için çağrı formu bulunamadı.');
        }
    };

    const handleArbitration = (row) => {
        const arbitrationId = Array.isArray(row.arbitration) && row.arbitration.length > 0 ? row.arbitration[0] : null;

        if (arbitrationId) {
            navigate(`/arbitration/${arbitrationId}`);
        } else {
            toast.error('Bu kaza dosyası için tahkim kaydı bulunamadı.');
        }
    };

    const columns = [
        {
            name: 'Müşteri',
            selector: row => row.customer?.companyName || row.customer?.clientName || '',
            sortable: true,
        },
        {
            name: 'Vergi / Kimlik No',
            selector: row => row.customer?.taxNumber || row.customer?.identityNumber || '',
            sortable: true,
        },
        { name: 'Dosya Numarası', selector: row => row.fileNumber || '', sortable: true },
        { name: 'Esas Numarası', selector: row => row.caseNumber || row.docketNumber || '', sortable: true },
        { name: 'Plaka', selector: row => row.accident?.licensePlate?.licensePlateNumber || '', sortable: true },
        { name: 'Kaza Tarihi', selector: row => formatDateTR(row.accident?.accidentDate), sortable: true },
        {
            name: 'Açık / Kapalı',
            selector: row => {
                if (typeof row.fileIsClosed === 'boolean') {
                    return row.fileIsClosed ? 'Kapalı' : 'Açık';
                }
                return row.fileStatus || '';
            },
            sortable: true,
        },
        {
            name: 'Dosya Türü',
            selector: row => {
                if (!Array.isArray(row.filesToBeWorked)) return '';
                const titles = row.filesToBeWorked
                    .map(item => {
                        if (Array.isArray(item.fileType) && item.fileType.length > 0) {
                            return item.fileType.map(ft => ft.title).filter(Boolean).join(', ');
                        }
                        return item.fileType?.title || '';
                    })
                    .filter(Boolean);
                return titles.join(', ');
            },
            sortable: true,
        },
        customerDetailButtonColumn({
            getCustomerId: (row) => row.customer?.id || row.customer?.[0],
            onClick: handleCustomerDetail
        }),
        actionButtonColumn({
            name: 'Tahkim',
            icon: Edit,
            onClick: handleArbitration,
            isVisible: (row) => Array.isArray(row.arbitration) && row.arbitration.length > 0
        }),
        actionButtonColumn({
            name: 'Çağrı Formu',
            icon: Edit,
            onClick: handleCallForm
        }),
    ];

    return (
        <>
            <Breadcrumbs parent='Müşteri Alanları' title='Kaza Dosyaları' />
            <Container fluid={true}>
                <Row>
                    <Col sm='12'>
                        <Card>
                            <CardHeader>
                                <H5>Kaza Dosyaları</H5>
                                <span>Tanımlanmış olan kaza dosyaları aşağıda listelenmektedir.</span>
                            </CardHeader>
                            <CardBody>
                                <Row className="g-3 mb-3 align-items-center">
                                    <CustomerNumberFilter
                                        value={customerNumberSearch}
                                        onChange={(e) => { setCustomerNumberSearch(e.target.value); setPage(1); }}
                                    />
                                    <CustomerNameFilter
                                        value={customerNameSearch}
                                        onChange={(e) => { setCustomerNameSearch(e.target.value); setPage(1); }}
                                    />
                                    <PlateFilter
                                        value={plateSearch}
                                        onChange={(e) => { setPlateSearch(e.target.value); setPage(1); }}
                                    />
                                    <AccidentDateFilter
                                        value={accidentDateSearch}
                                        onChange={(e) => { setAccidentDateSearch(e.target.value); setPage(1); }}
                                    />
                                </Row>

                                <Row className="g-3 mb-3 align-items-center">
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Esas Numarası</Label>
                                            <Input
                                                type="text"
                                                placeholder="Esas numarası (en az 3 karakter)"
                                                value={caseNumberSearch}
                                                onChange={(e) => { setCaseNumberSearch(e.target.value); setPage(1); }}
                                            />
                                        </FormGroup>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Dosya Durumu</Label>
                                            <Input
                                                type="select"
                                                value={selectedFileStatus}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setSelectedFileStatus(val);
                                                    const findGroup = (value) => {
                                                        if (!value) return null;
                                                        if (fileCaseStatusOptions.callForm.some(opt => opt.value === value)) return 'callForm';
                                                        if (fileCaseStatusOptions.nonEnforcement.some(opt => opt.value === value)) return 'nonEnforcement';
                                                        if (fileCaseStatusOptions.enforcement.some(opt => opt.value === value)) return 'enforcement';
                                                        return null;
                                                    };
                                                    setSelectedFileStatusGroup(findGroup(val));
                                                    setPage(1);
                                                }}
                                            >
                                                <option value="">Tümü</option>
                                                {[
                                                    { label: 'Çağrı Formu Durumları', options: fileCaseStatusOptions.callForm },
                                                    { label: 'Tahkim Durumları', options: fileCaseStatusOptions.nonEnforcement },
                                                    { label: 'İcra Durumları', options: fileCaseStatusOptions.enforcement }
                                                ].map(group => (
                                                    group.options?.length ? (
                                                        <optgroup key={group.label} label={group.label}>
                                                            {group.options.map(opt => (
                                                                <option key={`${group.label}-${opt.value}`} value={opt.value}>{opt.label}</option>
                                                            ))}
                                                        </optgroup>
                                                    ) : null
                                                ))}
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Açık / Kapalı</Label>
                                            <Input
                                                type="select"
                                                value={selectedOpenClosed}
                                                onChange={(e) => { setSelectedOpenClosed(e.target.value); setPage(1); }}
                                            >
                                                <option value="">Tümü</option>
                                                <option value="open">Açık</option>
                                                <option value="closed">Kapalı</option>
                                            </Input>
                                        </FormGroup>
                                    </Col>
                                    <Col md="3" sm="6">
                                        <FormGroup>
                                            <Label>Dosya Türü (çoklu)</Label>
                                            <Select
                                                isMulti
                                                id="fileTypeSearch"
                                                name="fileTypeSearch"
                                                options={fileTypeSelectOptions}
                                                value={selectedFileTypeSelectValues}
                                                onChange={(opts) => {
                                                    const values = Array.isArray(opts) ? opts.map(o => o.value) : [];
                                                    setSelectedFileTypes(values);
                                                    setPage(1);
                                                }}
                                                placeholder="Seçiniz..."
                                                classNamePrefix="select"
                                                noOptionsMessage={() => "Seçenek bulunamadı"}
                                                isClearable={true}
                                            />
                                        </FormGroup>
                                    </Col>
                                </Row>

                                <Fragment>
                                    <DataTable
                                        columns={columns}
                                        data={data}
                                        pagination
                                        paginationServer
                                        stickyActionPosition="left"
                                        selectableRows={false}
                                        paginationTotalRows={totalRows}
                                        paginationPerPage={pageSize}
                                        paginationDefaultPage={page}
                                        onChangePage={page => setPage(page)}
                                        onChangeRowsPerPage={pageSize => setPageSize(pageSize)}
                                        progressPending={isLoading}
                                        progressComponent={<div className="py-4 text-center">Yükleniyor...</div>}
                                        noDataComponent={isLoading ? null : "Kaza dosyası bulunamadı"}
                                    />
                                </Fragment>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default AccidentFiles;
